import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./lib/auth";
import { LanguageProvider } from "./lib/i18n";
import AppShell from "./components/AppShell";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import ResearchAssistant from "./pages/ResearchAssistant";
import Profile from "./pages/Profile";
import Pricing from "./pages/Pricing";
import ComingSoon from "./pages/ComingSoon";
import RegulatoryIntelligence from "./pages/RegulatoryIntelligence";
import CaseLawExplorer from "./pages/CaseLawExplorer";
import ComplianceChecker from "./pages/ComplianceChecker";
import LegalArgumentGenerator from "./pages/LegalArgumentGenerator";
import TreatyExplorer from "./pages/TreatyExplorer";
import ComparativeLaw from "./pages/ComparativeLaw";
import WatchdogsAlerts from "./pages/WatchdogsAlerts";
import Bulletins from "./pages/Bulletins";
import AiActWorkspace from "./pages/AiActWorkspace";
import GdprWorkspace from "./pages/GdprWorkspace";
import DoraWorkspace from "./pages/DoraWorkspace";
import Nis2Workspace from "./pages/Nis2Workspace";
import PasswordReset from "./pages/PasswordReset";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import { initGtag } from "./lib/gtag";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminResetLink from "./pages/AdminResetLink";
import BillingCheckout from "./pages/BillingCheckout";
import BillingPortal from "./pages/BillingPortal";
import SupportChatWidget from "./components/SupportChatWidget";
import CookieBanner from "./components/CookieBanner";
import About from "./pages/About";
import Security from "./pages/Security";
import Privacy from "./pages/Privacy";
import Legal from "./pages/Legal";
import Transparency from "./pages/Transparency";
import ScrollToTop from "./components/ScrollToTop";
import ForgotPassword from "./pages/ForgotPassword";
import Onboarding from "./pages/Onboarding";
import TeamWorkspace from "./pages/TeamWorkspace";
import InviteAccept from "./pages/InviteAccept";
import VerifyEmail from "./pages/VerifyEmail";
import RefundPolicy from "./pages/RefundPolicy";

function BulletinsAuthRedirect() {
    const { slug } = useParams();
    const next = encodeURIComponent(`/app/bulletins/${slug}`);
    return <Navigate to={`/login?next=${next}`} replace />;
}

function App() {
    useEffect(() => {
        initGtag();
    }, []);
    return (
        <AuthProvider>
            <LanguageProvider>
            <BrowserRouter>
                <ScrollToTop />
                <Toaster position="top-right" richColors />
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/tr" element={<Landing forcedLang="tr" />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/password-reset" element={<PasswordReset />} />
                    <Route path="/checkout/success/:sessionId" element={<CheckoutSuccess />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/bulletins" element={<Navigate to="/login?next=/app/bulletins" replace />} />
                    <Route path="/bulletins/:slug" element={<BulletinsAuthRedirect />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/security" element={<Security />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/transparency" element={<Transparency />} />
                    <Route path="/invite/:token" element={<InviteAccept />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/refunds" element={<RefundPolicy />} />

                    <Route path="/app" element={<AppShell />}>
                        <Route index element={<Dashboard />} />
                        <Route path="admin" element={<AdminDashboard />} />
                        <Route path="admin/analytics" element={<AdminAnalytics />} />
                        <Route path="admin/reset-link" element={<AdminResetLink />} />
                        <Route path="research" element={<ResearchAssistant />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="pricing" element={<Pricing />} />
                        <Route path="billing/checkout/:sessionId" element={<BillingCheckout />} />
                        <Route path="billing/portal" element={<BillingPortal />} />
                        <Route path="onboarding" element={<Onboarding />} />
                        <Route path="team" element={<TeamWorkspace />} />

                        <Route
                            path="regulatory"
                            element={<RegulatoryIntelligence />}
                        />
                        <Route
                            path="regulatory/:regId"
                            element={<RegulatoryIntelligence />}
                        />
                        <Route
                            path="case-law"
                            element={<CaseLawExplorer />}
                        />
                        <Route
                            path="case-law/:caseId"
                            element={<CaseLawExplorer />}
                        />
                        <Route
                            path="compliance"
                            element={<ComplianceChecker />}
                        />
                        <Route
                            path="treaty"
                            element={<TreatyExplorer />}
                        />
                        <Route
                            path="treaty/:slug"
                            element={<TreatyExplorer />}
                        />
                        <Route
                            path="treaty/:slug/article/:num"
                            element={<TreatyExplorer />}
                        />
                        <Route
                            path="comparative"
                            element={<ComparativeLaw />}
                        />
                        <Route
                            path="argument"
                            element={<LegalArgumentGenerator />}
                        />
                        <Route
                            path="watchdogs"
                            element={<WatchdogsAlerts />}
                        />
                        <Route path="ai-act" element={<AiActWorkspace />} />
                        <Route path="gdpr" element={<GdprWorkspace />} />
                        <Route path="dora" element={<DoraWorkspace />} />
                        <Route path="nis2" element={<Nis2Workspace />} />
                        <Route path="bulletins" element={<Bulletins />} />
                        <Route path="bulletins/:slug" element={<Bulletins />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <CookieBanner />
                <SupportChatWidget />
            </BrowserRouter>
            </LanguageProvider>
        </AuthProvider>
    );
}

export default App;
